import { Injectable, signal } from '@angular/core';

export interface SparkGlossaryEntry {
  term: string;
  definition: string;
}

/**
 * Plain-English explanations of TikTok's ad vocabulary.
 *
 * TikTok's three-level object model is unavoidable — the API requires it — so the
 * glossary carries the explanation rather than the form labels.
 */
export const SPARK_GLOSSARY: readonly SparkGlossaryEntry[] = [
  {
    term: 'Campaign',
    definition:
      'The top-level container. It holds your goal and an optional total spending cap. One goal per campaign — don’t mix.',
  },
  {
    term: 'Ad group',
    definition:
      'One audience plus one budget and schedule. Want to test two audiences? Two ad groups, same campaign.',
  },
  {
    term: 'Spark ad',
    definition:
      'A paid boost of a real organic post, running from the creator’s own handle. It keeps all the original likes, comments and shares — which is why it outperforms studio-made video.',
  },
  {
    term: 'Auth code',
    definition:
      'A one-time code the creator generates in TikTok to give you permission to advertise their post. It expires, and the post never leaves their account.',
  },
  {
    term: 'Objective',
    definition:
      'What you’re asking TikTok’s algorithm to optimise for. It changes who sees the ad far more than your targeting does.',
  },
  {
    term: 'Bid strategy',
    definition:
      'How aggressively you pay per result. “Lowest cost” spends your budget for the cheapest results available — the right default.',
  },
  {
    term: 'Daily vs lifetime budget',
    definition:
      'Daily spends roughly the same each day. Lifetime spreads a fixed pot across the whole schedule and lets TikTok lean into strong days.',
  },
  {
    term: 'Frequency cap',
    definition:
      'The maximum times one person can see your ad in a set window. Stops creator content going stale.',
  },
  {
    term: 'Attribution window',
    definition:
      'How long after seeing or clicking an ad a conversion still counts. SHOUT reports 7-day click / 1-day view by default.',
  },
] as const;

/** Open/closed state for the glossary drawer, shared so any link can open it. */
@Injectable({ providedIn: 'root' })
export class SparkGlossaryService {
  readonly isOpen = signal(false);
  readonly entries = SPARK_GLOSSARY;

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }
}
